import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CalendarIcon } from './icons'
import { Button } from './Button'
import { useAuth } from '../auth/AuthProvider'
import { useCapabilities } from '../auth/useCapabilities'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { useListOrgsQuery } from '@proaxiv/server-api'
import type { ListOrganizationsResponse, OrganizationSummary } from '@proaxiv/server-api'

/** Color tone for a nav item's icon badge — picked per item to aid quick visual scanning
 *  of the (otherwise monochrome) sidebar sections. Kept as a closed set of static class
 *  strings (see `ICON_TONE_CLASSES`) rather than interpolated so Tailwind can detect them. */
type IconTone =
  | 'blue'
  | 'green'
  | 'amber'
  | 'purple'
  | 'red'
  | 'orange'
  | 'teal'
  | 'indigo'
  | 'gray'

interface NavItem {
  to: string
  icon: React.ReactNode
  label: string
  tone: IconTone
}

interface NavSection {
  heading?: string
  items: NavItem[]
}

const ICON_TONE_CLASSES: Record<IconTone, string> = {
  blue: 'bg-pax-light-blue text-pax-blue',
  green: 'bg-pax-green/10 text-pax-green',
  amber: 'bg-pax-orange-warm/10 text-pax-orange-warm',
  // Note: `--color-pax-purple` is repurposed under the TMS brand as a neutral border shade
  // (`#d7dce3`, see `index.css`'s `[data-brand='triploms']` block), not an accent color — so
  // this tone renders as a washed-out gray/beige for TMS instead of a real purple. Kept for any
  // future PROAXIV-only usage, but nav items that must stay vividly colored on every brand
  // should use `indigo` instead (a plain Tailwind color, not a pax-* token, so no brand override
  // can wash it out).
  purple: 'bg-pax-purple/20 text-pax-purple',
  red: 'bg-pax-red/10 text-pax-red',
  orange: 'bg-pax-orange/10 text-pax-orange',
  // Not pax-* brand tokens — `teal`/`indigo` are used where a nav item must stay visibly
  // colored on every brand and the pax-* accents are already taken by neighboring items
  // (`teal` for Event Planner; `indigo` for Members, replacing `purple`, which TMS overrides
  // into a near-neutral gray).
  teal: 'bg-teal-500/10 text-teal-600',
  indigo: 'bg-indigo-500/10 text-indigo-600',
  gray: 'bg-gray-500/10 text-gray-500',
}

const HomeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5Z" />
    <path d="M9 21V12h6v9" />
  </svg>
)

const OrgIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
)

const UsersIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3Z" />
    <path d="M8 11c1.66 0 3-1.34 3-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3Z" />
    <path d="M8 13c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4Z" />
    <path d="M16 13c-.29 0-.62.02-.97.05C16.19 13.89 18 15.02 18 17v2h6v-2c0-2.66-5.33-4-8-4Z" />
  </svg>
)

const MenuIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12h18M3 6h18M3 18h18" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
)

/** Globe glyph — the common "language / translation" metaphor (browser language pickers,
 *  Google Translate, etc.), matching the admin tab now that it's scoped entirely to managing
 *  translations rather than generic settings (issue #204). */
const TranslateIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <path d="M12 3a15.3 15.3 0 0 1 4 9 15.3 15.3 0 0 1-4 9 15.3 15.3 0 0 1-4-9 15.3 15.3 0 0 1 4-9z" />
  </svg>
)

const BuildingIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18M3 9h18M3 15h18" />
  </svg>
)

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const UserIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const LogoutIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

/** Building glyph + a "+" badge (same plus placement/style as OrgMembersPanel's UserPlusIcon)
 *  so "New Organization" reads unambiguously as "add an organization" instead of a bare "+"
 *  that people were mistaking for a generic/unrelated add action. */
const OrgPlusIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="4" width="13" height="16" rx="1.5" />
    <path d="M2 9.5h13M2 14.5h13M8.5 4v16" />
    <line x1="20" y1="8" x2="20" y2="14" />
    <line x1="23" y1="11" x2="17" y2="11" />
  </svg>
)

/** Mobile drawer org switcher: collapse to this many entries until "See others" is tapped. */
const MOBILE_ORGS_COLLAPSED_COUNT = 3

interface Props {
  open: boolean
  onClose: () => void
  onToggle: () => void
}

/** Collapsible left-side navigation sidebar.
 *  Mobile (< md): persistent icon rail (44px) that expands into a labeled drawer overlay;
 *  the Menu/Close control stays fixed at the top of the rail.
 *  Desktop (>= md): persistent rail that collapses to icon-only (60px) or expands (260px). */
export function Sidebar({ open, onClose, onToggle }: Props) {
  const { t } = useTranslation()
  const { username, logout } = useAuth()
  const { isAdmin, canCreateEvent } = useCapabilities()
  const { activeOrgId, setActiveOrgId, activeOrgCapabilities, activeOrgFeatures } = useActiveOrg()
  const { data: rawOrgsData } = useListOrgsQuery({})
  const orgs: OrganizationSummary[] =
    (rawOrgsData as ListOrganizationsResponse | undefined)?.organizations ?? []
  const isMobile = () => window.matchMedia('(max-width: 767px)').matches
  const [orgsExpanded, setOrgsExpanded] = useState(false)

  // Always start collapsed each time the drawer is (re)opened, rather than remembering the
  // previous expand/collapse state across opens. Adjusted during render (rather than in an
  // effect) to avoid the extra render pass that setState-in-effect would trigger.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setOrgsExpanded(false)
  }

  // The active org always leads the list — otherwise it can land past the collapsed
  // MOBILE_ORGS_COLLAPSED_COUNT cutoff (or just be hard to spot further down once expanded),
  // forcing an extra "See others" tap just to confirm which org is currently selected.
  // `.sort()` is stable, so the relative order of the other orgs is preserved.
  const orderedOrgs = [...orgs].sort((a, b) => {
    if (a.orgId === activeOrgId) return -1
    if (b.orgId === activeOrgId) return 1
    return 0
  })
  const visibleOrgs =
    orgsExpanded || orderedOrgs.length <= MOBILE_ORGS_COLLAPSED_COUNT
      ? orderedOrgs
      : orderedOrgs.slice(0, MOBILE_ORGS_COLLAPSED_COUNT)

  const navSections: NavSection[] = [
    {
      items: [
        { to: '/home', icon: <HomeIcon />, label: t('nav_home'), tone: 'blue' },
        // Once the user already belongs to an organization, "New Organization" moves down
        // into the organization list itself (bottom of the mobile drawer list / desktop
        // switcher dropdown) instead of sitting in the main nav — see issue #200. It stays
        // here, as the only way in, for a brand-new user who has never created one yet.
        ...(orgs.length === 0
          ? [
              {
                to: '/organizations/new',
                icon: <OrgPlusIcon />,
                label: t('nav_new_organization'),
                tone: 'green' as const,
              },
            ]
          : []),
      ],
    },
    ...(isAdmin
      ? [
          {
            heading: t('nav_section_administration'),
            items: [
              {
                to: '/organizations/list',
                icon: <OrgIcon />,
                label: t('nav_administration'),
                tone: 'amber' as const,
              },
              {
                to: '/settings',
                icon: <TranslateIcon />,
                label: t('nav_translations'),
                tone: 'purple' as const,
              },
            ],
          },
        ]
      : []),
    // Advanced Settings (`/org/security` — org disable/delete) is intentionally not linked
    // from the sidebar; the route remains reachable by URL if needed (issue #217).
    ...(activeOrgCapabilities.canUpdateOrg
      ? [
          {
            heading: t('nav_section_organization'),
            items: [
              { to: '/users', icon: <UsersIcon />, label: t('nav_users'), tone: 'indigo' as const },
            ],
          },
        ]
      : []),
    ...(canCreateEvent && activeOrgFeatures.includes('eventplanner')
      ? [
          {
            heading: t('nav_section_functionalities'),
            items: [
              {
                to: '/event-planner',
                icon: <CalendarIcon size={20} />,
                label: t('nav_event_planner'),
                tone: 'teal' as const,
              },
            ],
          },
        ]
      : []),
  ]

  return (
    <>
      {/* Backdrop — mobile only; tapping it closes the drawer */}
      {open && (
        <div
          className="bg-pax-black/20 fixed inset-0 z-20 backdrop-blur-[1px] md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          // Above the header (z-40) so the top-left Menu/Close control is never covered when
          // the drawer expands under the fixed topbar.
          // `transform-gpu` keeps this on its own compositor layer so mobile Chrome/Safari
          // doesn't visibly drift it during the address-bar hide/show scroll animation (see
          // AppLayout's decorative background for the same fix/rationale).
          'border-pax-purple/25 fixed top-0 left-0 z-50 flex h-full transform-gpu flex-col border-r bg-white transition-all duration-200',
          // Mobile: persistent icon-only rail (44px) at rest, always visible instead of sliding
          // fully off-canvas — expands into the full labeled drawer overlay when toggled open.
          'translate-x-0',
          open ? 'w-[220px] shadow-[4px_0_24px_rgba(10,124,255,0.08)]' : 'w-11 shadow-none',
          // Desktop: persistent rail — always visible, width controlled by open state
          'md:translate-x-0',
          open
            ? 'md:w-[260px] md:shadow-[4px_0_24px_rgba(10,124,255,0.08)]'
            : 'md:w-[60px] md:shadow-none',
        ].join(' ')}
        aria-label={t('nav_aria_sidebar')}
      >
        {/* Top row — toggle pinned to the top-left rail slot. The Admin badge is not
            duplicated here: the Header's badge is always visible (including on mobile), so
            showing a second one in the drawer would just repeat it. */}
        <div
          className={[
            'flex h-16 shrink-0 items-center',
            open ? 'border-pax-purple/20 border-b md:border-0' : '',
          ].join(' ')}
        >
          <Button
            type="button"
            variant="unstyled"
            onClick={onToggle}
            className={[
              'text-pax-gray hover:text-pax-blue flex h-16 shrink-0 items-center justify-center transition-colors focus-visible:outline-none',
              open ? 'w-11 md:w-[60px]' : 'w-full',
            ].join(' ')}
            aria-label={open ? t('nav_aria_close') : t('nav_aria_open')}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </Button>
        </div>

        {/* Mobile collapsed rail: keep an org-selection affordance (same grid icon as in the
            expanded list / header switcher). Tapping it opens the drawer to the org list. */}
        {!open && orgs.length > 0 && (
          <div className="border-pax-purple/20 border-b px-2 pb-2 md:hidden">
            <button
              type="button"
              onClick={onToggle}
              title="Your organizations"
              aria-label="Your organizations"
              className="hover:bg-pax-light-purple hover:text-pax-blue text-pax-black flex w-full cursor-pointer items-center justify-center rounded-xl px-2 py-2.5 transition-colors focus-visible:outline-none"
            >
              <span className="flex h-4 w-4 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                <BuildingIcon />
              </span>
            </button>
          </div>
        )}

        {/* Scrollable drawer body: the mobile org switcher and the nav sections scroll
            together as a single area (one scrollbar), rather than each being its own separate
            fixed-height scroll box — only the toggle row above and the account footer below
            stay pinned outside this. */}
        <div className="flex flex-1 flex-col overflow-x-hidden overflow-y-auto">
          {/* Mobile-only: organization switcher, moved out of the topbar (which has no room to
              spare on narrow phones) and into the drawer as a plain list instead of a floating
              dropdown. Hidden entirely from md up, where this lives in the Header, and while the
              rail is collapsed (labels don't fit in the icon-only width). */}
          <div
            className={[
              'border-pax-purple/20 shrink-0 border-b px-2 pb-3 md:hidden',
              open ? '' : 'hidden',
            ].join(' ')}
          >
            {orgs.length > 0 && (
              <>
                <p className="text-pax-gray px-2 pt-3 pb-1 text-[10px] font-semibold tracking-widest">
                  YOUR ORGANIZATIONS
                </p>
                <div className="flex flex-col gap-0.5">
                  {visibleOrgs.map((org) => (
                    <button
                      key={org.orgId}
                      type="button"
                      onClick={() => setActiveOrgId(org.orgId)}
                      aria-current={org.orgId === activeOrgId}
                      className={[
                        'flex cursor-pointer items-center gap-2.5 rounded-xl px-2 py-2 text-left text-xs font-medium transition-colors',
                        org.orgId === activeOrgId
                          ? 'bg-pax-light-purple text-pax-blue'
                          : 'hover:bg-pax-light-purple hover:text-pax-blue text-pax-black',
                      ].join(' ')}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        <BuildingIcon />
                      </span>
                      <span className="min-w-0 flex-1 truncate">{org.name}</span>
                      {org.orgId === activeOrgId && (
                        <span className="shrink-0">
                          <CheckIcon />
                        </span>
                      )}
                    </button>
                  ))}
                  {orgs.length > MOBILE_ORGS_COLLAPSED_COUNT && (
                    <button
                      type="button"
                      onClick={() => setOrgsExpanded((prev) => !prev)}
                      className="text-pax-gray hover:text-pax-blue flex cursor-pointer items-center gap-1.5 rounded-xl px-2 py-2 text-left text-xs font-medium transition-colors"
                    >
                      <span
                        className={[
                          'flex h-4 w-4 shrink-0 items-center justify-center transition-transform',
                          orgsExpanded ? 'rotate-180' : '',
                        ].join(' ')}
                      >
                        <ChevronDownIcon />
                      </span>
                      {orgsExpanded ? 'See less' : 'See others'}
                    </button>
                  )}
                  {/* "New Organization" lives at the bottom of the org list once the user
                      already has at least one — see issue #200. */}
                  <NavLink
                    to="/organizations/new"
                    onClick={() => {
                      if (isMobile()) onClose()
                    }}
                    className="text-pax-green hover:bg-pax-green/10 flex cursor-pointer items-center gap-2.5 rounded-xl px-2 py-2 text-left text-xs font-medium transition-colors"
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                      <OrgPlusIcon />
                    </span>
                    {t('nav_new_organization')}
                  </NavLink>
                </div>
              </>
            )}
          </div>

          {/* Nav */}
          <nav className="flex flex-col gap-1 px-2 pb-4">
            {navSections.map((section, si) => (
              <div key={si} className="mb-1">
                {open && section.heading && (
                  <p className="text-pax-gray px-2 pt-3 pb-1 text-[10px] font-semibold tracking-widest">
                    {section.heading}
                  </p>
                )}
                {!open && section.heading && <div className="border-pax-purple/20 my-1 border-t" />}
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={!open ? item.label : undefined}
                    onClick={() => {
                      // Close the drawer after navigating on mobile; leave desktop rail state alone
                      if (isMobile()) onClose()
                    }}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-xl py-2.5 text-sm font-medium transition-colors',
                        // Collapsed rail: no horizontal padding + centered, so the icon badge sits
                        // squarely in the narrow icon-only column (with padding, the badge's fixed
                        // size didn't leave enough room and rendered off-center on mobile).
                        open ? 'px-2' : 'justify-center px-0',
                        isActive
                          ? 'bg-pax-light-purple text-pax-blue'
                          : 'hover:bg-pax-light-purple hover:text-pax-blue text-pax-black',
                      ].join(' ')
                    }
                  >
                    {/* Icons render a touch smaller in the mobile icon rail than in the
                      desktop rail/expanded drawer, to fit the narrower collapsed width. Each
                      item's icon sits in a tone-tinted badge (see ICON_TONE_CLASSES) so the
                      otherwise monochrome nav is easier to scan at a glance. */}
                    <span
                      className={[
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg md:h-8 md:w-8',
                        '[&>svg]:h-4 [&>svg]:w-4 md:[&>svg]:h-[18px] md:[&>svg]:w-[18px]',
                        ICON_TONE_CLASSES[item.tone],
                      ].join(' ')}
                    >
                      {item.icon}
                    </span>
                    {/* Long labels (e.g. Japanese) wrap onto up to 3 lines, breaking mid-word if
                      needed, instead of being clipped to a single ellipsized line. */}
                    {open && <span className="line-clamp-3 min-w-0 break-words">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </div>

        {/* Mobile-only account footer: email + sign out. The header's user dropdown is
            hidden below md (see Header.tsx), so this drawer is the only place these are
            reachable on mobile. A single flat-edged box (border + ring + shadow, no rounding)
            so it reads as one distinct block from the nav list above it, not a card floating
            inside a card. */}
        <div
          className={[
            'border-pax-purple/50 flex flex-col border shadow-lg ring-1 ring-black/5 md:hidden',
            open ? '' : 'hidden',
          ].join(' ')}
        >
          <div className="flex items-center gap-2.5 px-3 py-2.5">
            <span className="bg-pax-light-purple text-pax-blue flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
              <UserIcon />
            </span>
            <span className="text-pax-black min-w-0 flex-1 truncate text-xs font-medium">
              {username}
            </span>
          </div>
          <button
            type="button"
            onClick={logout}
            className="text-pax-red border-pax-purple/20 hover:bg-pax-red/5 flex w-full cursor-pointer items-center gap-2.5 border-t px-3 py-2.5 text-left text-xs font-medium transition-colors"
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              <LogoutIcon />
            </span>
            {t('header_signout')}
          </button>
        </div>
      </aside>
    </>
  )
}
